import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle, XCircle, Clock, FloppyDisk } from "@phosphor-icons/react";

const STATUSES = [
  { value: "present", label: "Présent", icon: CheckCircle, color: "text-emerald-600" },
  { value: "absent", label: "Absent", icon: XCircle, color: "text-destructive" },
  { value: "late", label: "En retard", icon: Clock, color: "text-orange-500" },
  { value: "excused", label: "Absence justifiée", icon: CheckCircle, color: "text-blue-600" },
];

export default function Attendance() {
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [classId, setClassId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [existing, setExisting] = useState([]);
  const [drafts, setDrafts] = useState({});

  useEffect(() => { api.get("/classes").then(r => setClasses(r.data)); }, []);

  useEffect(() => {
    if (!classId) { setStudents([]); return; }
    api.get("/students", { params: { class_id: classId } }).then(r => setStudents(r.data));
  }, [classId]);

  useEffect(() => {
    if (!classId) return;
    api.get("/attendance", { params: { class_id: classId, date_from: date, date_to: date } })
      .then(r => setExisting(r.data));
  }, [classId, date]);

  const existingByStudent = useMemo(() => {
    const m = {}; existing.forEach(a => { m[a.student_id] = a; }); return m;
  }, [existing]);

  const set = (student_id, status) => setDrafts(prev => ({ ...prev, [student_id]: status }));

  const markAll = (status) => {
    const next = {};
    students.forEach(s => { next[s.id] = status; });
    setDrafts(next);
  };

  const save = async () => {
    const records = Object.entries(drafts).map(([student_id, status]) => ({
      student_id, class_id: classId, date, status, note: "",
    }));
    if (!records.length) { toast.info("Aucun changement à enregistrer"); return; }
    try {
      await api.post("/attendance/bulk", records);
      toast.success(`${records.length} présences enregistrées`);
      setDrafts({});
      const { data } = await api.get("/attendance", { params: { class_id: classId, date_from: date, date_to: date } });
      setExisting(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      <header className="mb-6">
        <div className="text-xs uppercase tracking-widest text-secondary font-semibold">Assiduité</div>
        <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight">Appel journalier</h1>
      </header>

      <Card className="p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label>Classe</Label>
          <Select value={classId} onValueChange={setClassId}>
            <SelectTrigger data-testid="select-att-class"><SelectValue placeholder="Choisir…" /></SelectTrigger>
            <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Date</Label>
          <Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="input-att-date" />
        </div>
        <div className="flex items-end gap-2">
          <Button variant="outline" className="flex-1 rounded-full" onClick={() => markAll("present")} data-testid="btn-mark-all-present">Tout présent</Button>
          <Button onClick={save} className="flex-1 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-save-attendance">
            <FloppyDisk size={16} className="mr-2" /> Enregistrer
          </Button>
        </div>
      </Card>

      {classId ? (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3">Élève</th>
              <th className="px-4 py-3">Actuel</th>
              <th className="px-4 py-3 text-right">Statut</th>
            </tr></thead>
            <tbody>
              {students.map((s, i) => {
                const current = drafts[s.id] || existingByStudent[s.id]?.status;
                return (
                  <tr key={s.id} className={`border-b border-border last:border-0 ${i % 2 ? "bg-muted/30" : ""}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{s.first_name} {s.last_name}</div>
                      <div className="text-xs text-muted-foreground font-mono">{s.matricule}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {existingByStudent[s.id] ? (
                        <span className="uppercase tracking-wider font-semibold">{STATUSES.find(x => x.value === existingByStudent[s.id].status)?.label}</span>
                      ) : <span className="text-muted-foreground italic">non renseigné</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1 flex-wrap">
                        {STATUSES.map(st => (
                          <Button
                            key={st.value} variant={current === st.value ? "default" : "outline"} size="sm"
                            onClick={() => set(s.id, st.value)}
                            data-testid={`btn-att-${s.id}-${st.value}`}
                            className={current === st.value ? "bg-primary text-primary-foreground" : ""}
                          >
                            <st.icon size={14} className="mr-1" /> {st.label}
                          </Button>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!students.length && <tr><td colSpan={3} className="px-4 py-12 text-center text-sm text-muted-foreground">Aucun élève dans cette classe.</td></tr>}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center text-sm text-muted-foreground py-12">Choisissez une classe pour commencer l'appel.</div>
      )}
    </div>
  );
}
